import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Alert, CircularProgress, Chip, Divider
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import axios from 'axios';
import { nombreTipoDocumento } from '../utils/whatsapp';

const TIPOS_DOC = [
  { value: null,   label: 'Por defecto (todos los documentos)' },
  { value: '01',   label: 'Factura Electrónica (01)' },
  { value: '03',   label: 'Boleta de Venta (03)' },
  { value: '07',   label: 'Nota de Crédito (07)' },
  { value: '08',   label: 'Nota de Débito (08)' },
  { value: '09',   label: 'Guía de Remisión Remitente (09)' },
  { value: '31',   label: 'Guía de Remisión Transportista (31)' },
  { value: 'CTZ',  label: 'Cotización' },
];

const VARIABLES = [
  { var: '{{nombre_empresa}}',       label: 'Empresa' },
  { var: '{{nombre_cliente}}',       label: 'Cliente' },
  { var: '{{serie}}',                label: 'Serie' },
  { var: '{{numero}}',               label: 'Número' },
  { var: '{{fecha}}',                label: 'Fecha' },
  { var: '{{total}}',                label: 'Total' },
  { var: '{{moneda}}',               label: 'Moneda' },
  { var: '{{link_pdf}}',             label: 'Link PDF' },
  { var: '{{tipo_documento_nombre}}', label: 'Tipo doc.' },
];

const EMOJIS_COMUNES = ['✅','📄','📋','📅','💰','🔗','🚛','📦','👤','🏢','📞','✉️','⚠️','❌','🎉','👋','🙏','⭐','💯','✨'];

// Preview renderizando formato WhatsApp
function PreviewMensaje({ texto }) {
  const html = (texto || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br/>');
  return (
    <Box sx={{
      bgcolor: '#e8fce8', border: '1px solid #c8e6c9', borderRadius: 2,
      p: 1.5, fontSize: '0.82rem', lineHeight: 1.6, fontFamily: 'inherit',
      minHeight: 80, maxHeight: 280, overflowY: 'auto', color: '#1a1a1a',
    }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Inserta texto en el textarea en la posición del cursor
function insertarEnCursor(ref, texto, setValor, valor) {
  const el = ref.current;
  if (!el) { setValor(v => v + texto); return; }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const nuevo = valor.substring(0, start) + texto + valor.substring(end);
  setValor(nuevo);
  setTimeout(() => {
    el.selectionStart = el.selectionEnd = start + texto.length;
    el.focus();
  }, 0);
}

const EMPTY_FORM = { tipo_documento: null, nombre: 'Plantilla por defecto', plantilla_texto: '' };

export default function ConfiguracionWhatsApp() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openEditor, setOpenEditor] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [preview, setPreview] = useState(false);
  const textareaRef = React.useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [pr, dr] = await Promise.all([
        axios.get('/api/wsp-config/plantillas'),
        axios.get('/api/wsp-config/defaults'),
      ]);
      setPlantillas(Array.isArray(pr.data) ? pr.data : []);
      setDefaults(dr.data || {});
    } catch (e) {
      setError('Error al cargar plantillas: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = (tipo = null) => {
    const def = tipo ? (defaults[tipo] || defaults['DEFAULT'] || '') : (defaults['DEFAULT'] || '');
    const tipoInfo = TIPOS_DOC.find(t => t.value === tipo);
    setEditForm({
      tipo_documento: tipo,
      nombre: tipoInfo ? tipoInfo.label : 'Plantilla por defecto',
      plantilla_texto: def,
    });
    setPreview(false);
    setOpenEditor(true);
  };

  const abrirEditar = (p) => {
    setEditForm({
      id: p.id,
      tipo_documento: p.tipo_documento,
      nombre: p.nombre,
      plantilla_texto: p.plantilla_texto,
    });
    setPreview(false);
    setOpenEditor(true);
  };

  const handleGuardar = async () => {
    if (!editForm.plantilla_texto.trim()) { setError('El texto de la plantilla no puede estar vacío.'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post('/api/wsp-config/plantillas', {
        tipo_documento: editForm.tipo_documento || null,
        nombre: editForm.nombre,
        plantilla_texto: editForm.plantilla_texto,
      });
      setSuccess('Plantilla guardada correctamente.');
      setOpenEditor(false);
      cargar();
    } catch (e) {
      setError('Error al guardar: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await axios.delete(`/api/wsp-config/plantillas/${id}`);
      setSuccess('Plantilla eliminada.');
      cargar();
    } catch (e) {
      setError('Error al eliminar: ' + (e.response?.data?.error || e.message));
    }
  };

  const restaurarDefault = () => {
    const tipo = editForm.tipo_documento;
    const def = tipo ? (defaults[tipo] || defaults['DEFAULT'] || '') : (defaults['DEFAULT'] || '');
    setEditForm(f => ({ ...f, plantilla_texto: def }));
  };

  // Insertar formato en el cursor
  const insertarFormato = (prefijo, sufijo = prefijo) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const sel = editForm.plantilla_texto.substring(start, end);
    const nuevo = editForm.plantilla_texto.substring(0, start) +
      prefijo + (sel || 'texto') + sufijo +
      editForm.plantilla_texto.substring(end);
    setEditForm(f => ({ ...f, plantilla_texto: nuevo }));
    setTimeout(() => {
      textarea.selectionStart = start + prefijo.length;
      textarea.selectionEnd = start + prefijo.length + (sel || 'texto').length;
      textarea.focus();
    }, 0);
  };

  const insertarTexto = (texto) => {
    insertarEnCursor(textareaRef, texto, v => setEditForm(f => ({ ...f, plantilla_texto: v })), editForm.plantilla_texto);
  };

  const tipoLabel = (tipo) => {
    if (!tipo) return 'Por defecto (todos)';
    return TIPOS_DOC.find(t => t.value === tipo)?.label || tipo;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <WhatsAppIcon sx={{ color: '#25d366', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Plantillas de WhatsApp</Typography>
          <Typography variant="body2" color="text.secondary">
            Personaliza los mensajes enviados por WhatsApp para cada tipo de documento
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Acciones rápidas para crear plantillas */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#f9fbe7' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Crea plantillas específicas por tipo de documento. Si no hay una específica, se usará la plantilla <strong>Por defecto</strong>.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {TIPOS_DOC.map(t => {
            const existe = plantillas.some(p => (p.tipo_documento === t.value) || (!p.tipo_documento && t.value === null));
            return (
              <Button
                key={t.value ?? 'default'}
                size="small"
                variant={existe ? 'contained' : 'outlined'}
                color={t.value === null ? 'success' : 'primary'}
                startIcon={existe ? <EditIcon /> : <AddIcon />}
                onClick={() => {
                  const existing = plantillas.find(p => p.tipo_documento === t.value || (!p.tipo_documento && t.value === null));
                  existing ? abrirEditar(existing) : abrirNuevo(t.value);
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {t.label}
              </Button>
            );
          })}
        </Box>
      </Paper>

      {/* Tabla de plantillas existentes */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: '#25d366' }} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo de documento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Preview del mensaje</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plantillas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No hay plantillas configuradas. Crea una usando los botones de arriba.
                    </TableCell>
                  </TableRow>
                ) : plantillas.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={tipoLabel(p.tipo_documento)}
                        color={!p.tipo_documento ? 'success' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{p.nombre}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="caption" color="text.secondary" sx={{
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        lineHeight: 1.4, whiteSpace: 'pre-line',
                      }}>
                        {p.plantilla_texto}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" color="primary" onClick={() => abrirEditar(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleEliminar(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog editor de plantilla */}
      <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsAppIcon sx={{ color: '#25d366' }} />
            <Typography fontWeight="bold">
              {editForm.id ? 'Editar plantilla' : 'Nueva plantilla'} — {tipoLabel(editForm.tipo_documento)}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Nombre */}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Nombre de la plantilla"
                value={editForm.nombre}
                onChange={e => setEditForm(f => ({ ...f, nombre: e.target.value }))}
              />
            </Grid>

            {/* Barra de formato */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">FORMATO</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Tooltip title="Negrita (*texto*)">
                  <IconButton size="small" onClick={() => insertarFormato('*')}><FormatBoldIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Cursiva (_texto_)">
                  <IconButton size="small" onClick={() => insertarFormato('_')}><FormatItalicIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Tachado (~texto~)">
                  <IconButton size="small" onClick={() => insertarFormato('~')}><StrikethroughSIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Typography variant="caption" color="text.secondary">Emojis:</Typography>
                {EMOJIS_COMUNES.map(e => (
                  <Tooltip key={e} title={e}>
                    <IconButton size="small" onClick={() => insertarTexto(e)} sx={{ fontSize: '1rem', p: '2px' }}>
                      {e}
                    </IconButton>
                  </Tooltip>
                ))}
              </Box>
            </Grid>

            {/* Variables disponibles */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">INSERTAR VARIABLE</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {VARIABLES.map(v => (
                  <Chip
                    key={v.var}
                    label={v.label}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => insertarTexto(v.var)}
                    sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>

            {/* Editor / Preview toggle */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {preview ? 'PREVIEW' : 'EDITOR'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="text" onClick={restaurarDefault} startIcon={<RestoreIcon />}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                    Restaurar predeterminado
                  </Button>
                  <Button size="small" variant={preview ? 'contained' : 'outlined'}
                    onClick={() => setPreview(v => !v)} sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
                    {preview ? 'Editar' : 'Ver preview'}
                  </Button>
                </Box>
              </Box>

              {preview ? (
                <PreviewMensaje texto={editForm.plantilla_texto} />
              ) : (
                <TextField
                  inputRef={textareaRef}
                  multiline rows={10} fullWidth size="small"
                  value={editForm.plantilla_texto}
                  onChange={e => setEditForm(f => ({ ...f, plantilla_texto: e.target.value }))}
                  placeholder="Escribe el mensaje aquí. Usa *negrita*, _cursiva_, ~tachado~ y {{variables}}"
                  inputProps={{ style: { fontSize: '0.82rem', lineHeight: 1.6, fontFamily: 'monospace' } }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Las variables entre {'{{...}}'} se reemplazarán con los datos del documento al enviar.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenEditor(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleGuardar} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <WhatsAppIcon />}
            sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1ebe59' } }}
          >
            {saving ? 'Guardando...' : 'Guardar plantilla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
